import {
  FC,
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsLeft, ChevronsRight, TriangleAlert } from 'lucide-react';

import {
  BerthBlock,
  BerthMove,
  ClampedMove,
  clampMove,
  formatBlockTime,
  getBlockSpanPct,
  getMovesLabel,
  isZeroMove,
  moveShipVisit,
  msToPx,
  pixelDeltaToMove,
  SNAP_MINUTES,
  toBerthBlock,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import { STEPS_SELECTION_ATTRIBUTE } from '@/components/blocks/berth-plan/berth-plan-toolbar';
import { BerthPlanVisit } from '@/components/blocks/berth-plan/types';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn, formatDateForDisplay } from '@/lib/utils';

const KEY_MOVES: Record<string, BerthMove> = {
  ArrowLeft: { bollardDelta: 1, minutesDelta: 0 },
  ArrowRight: { bollardDelta: -1, minutesDelta: 0 },
  ArrowUp: { bollardDelta: 0, minutesDelta: -SNAP_MINUTES },
  ArrowDown: { bollardDelta: 0, minutesDelta: SNAP_MINUTES },
};

const CUTOFF_BLOCKED_HINT_MS = 1500;
const TOOLTIP_DELAY_MS = 400;

interface BerthPlanBlockProps {
  block: BerthBlock;
  /** Time at the top of the axis; a block starting earlier is clipped there. */
  startMs: number;
  pxPerHour: number;
  cutoffMs?: number;
  hasConflict: boolean;
  /** Matches the Berth Plan search. */
  isMatch: boolean;
  /** A search is active and this visit doesn't match it. */
  isDimmed: boolean;
  isSelected: boolean;
  onSelect: (visitId: string) => void;
  onDeselect: () => void;
  /** Popover body, rendered while selected. */
  editor: ReactNode;
  getPxPerBollard: () => number;
  getScrollTop: () => number;
  /** Absent on a read-only plan: no dragging or keyboard moves. */
  onMove?: (visitId: string, move: BerthMove) => void;
}

const BerthPlanBlock: FC<BerthPlanBlockProps> = ({
  block,
  startMs,
  pxPerHour,
  cutoffMs,
  hasConflict,
  isMatch,
  isDimmed,
  isSelected,
  onSelect,
  onDeselect,
  editor,
  getPxPerBollard,
  getScrollTop,
  onMove,
}) => {
  const { t, i18n } = useTranslation();
  const { visit } = block;
  const canMove = onMove !== undefined && !block.isLocked;
  const blockRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{
    x: number;
    y: number;
    scrollTop: number;
    pxPerBollard: number;
  } | null>(null);
  // A drag ends in a click; this keeps that click from opening the editor.
  const wasDragged = useRef(false);
  // Tied to the visit it was made for: once the dropped move lands in the
  // cache the visit changes and the stale preview drops away without flicker.
  const [preview, setPreview] = useState<
    (ClampedMove & { visit: BerthPlanVisit }) | null
  >(null);
  const [keyBlocked, setKeyBlocked] = useState(false);
  const current = preview?.visit === visit ? preview : null;
  const move = current?.move ?? null;

  // Brings a visit picked by search or the conflict stepper into view.
  useEffect(() => {
    if (isSelected) blockRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isSelected]);

  useEffect(() => {
    if (!keyBlocked) return;
    const timeout = window.setTimeout(() => {
      setKeyBlocked(false);
    }, CUTOFF_BLOCKED_HINT_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [keyBlocked]);

  const moveFromPointer = (event: PointerEvent): ClampedMove | null => {
    const start = dragStart.current;
    if (!start || start.pxPerBollard === 0) return null;
    return clampMove(
      block,
      pixelDeltaToMove(
        event.clientX - start.x,
        event.clientY - start.y + getScrollTop() - start.scrollTop,
        start.pxPerBollard,
        pxPerHour,
        block.arrivalMs
      ),
      cutoffMs
    );
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!canMove || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
      scrollTop: getScrollTop(),
      pxPerBollard: getPxPerBollard(),
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const next = moveFromPointer(event);
    if (!next) return;
    setPreview((prev) =>
      prev?.visit === visit &&
      prev.move.bollardDelta === next.move.bollardDelta &&
      prev.move.minutesDelta === next.move.minutesDelta &&
      prev.isCutoffBlocked === next.isCutoffBlocked
        ? prev
        : { visit, ...next }
    );
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const next = moveFromPointer(event);
    dragStart.current = null;
    if (!next || isZeroMove(next.move)) {
      setPreview(null);
      return;
    }
    wasDragged.current = true;
    onMove?.(visit.id, next.move);
  };

  const handlePointerCancel = () => {
    dragStart.current = null;
    setPreview(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(visit.id);
      return;
    }
    const keyMove = KEY_MOVES[event.key];
    if (!canMove || !keyMove) return;
    event.preventDefault();
    const clamped = clampMove(block, keyMove, cutoffMs);
    if (clamped.isCutoffBlocked) setKeyBlocked(true);
    if (!isZeroMove(clamped.move)) onMove(visit.id, clamped.move);
  };

  // Geometry follows the drag preview; the stored block stays the reference.
  const shown = move
    ? (toBerthBlock(moveShipVisit(visit, move), cutoffMs) ?? block)
    : block;
  const { startPct, widthPct } = getBlockSpanPct(shown);
  const isBlocked = current?.isCutoffBlocked === true || keyBlocked;

  const moves = getMovesLabel(visit);
  const movesText = moves
    ? t(`berthPlan.${moves.key}`, {
        discharge: moves.discharge,
        load: moves.load,
      })
    : '';
  const labelValues = {
    shipId: visit.ship_id,
    service: visit.shipping_line_id,
    fore: visit.bollard_fore,
    aft: visit.bollard_aft,
    arrival: formatDateForDisplay(visit.arrival_time),
    departure: formatDateForDisplay(visit.departure_time),
  };
  const label = moves
    ? t('berthPlan.blockLabelWithMoves', {
        ...labelValues,
        moves: movesText,
      })
    : t('berthPlan.blockLabel', labelValues);

  return (
    <Popover
      open={isSelected && Boolean(editor)}
      onOpenChange={(open) => {
        if (!open) onDeselect();
      }}>
      <Tooltip
        delayDuration={TOOLTIP_DELAY_MS}
        open={isSelected || move ? false : undefined}>
        <PopoverAnchor asChild>
          <TooltipTrigger asChild>
            <div
              ref={blockRef}
              role='button'
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={label}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              onKeyDown={handleKeyDown}
              onClick={() => {
                if (wasDragged.current) {
                  wasDragged.current = false;
                  return;
                }
                onSelect(visit.id);
              }}
              className={cn(
                'absolute z-10 touch-none select-none overflow-hidden rounded-sm border px-1.5 py-0.5 text-[10px] leading-tight transition-opacity',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                block.isLocked
                  ? 'border-border bg-muted/70 text-muted-foreground'
                  : 'border-primary/50 bg-primary/15',
                canMove && 'cursor-grab active:cursor-grabbing',
                hasConflict && 'border-2 border-destructive bg-destructive/10',
                isMatch && 'ring-2 ring-blue-500',
                isDimmed && 'opacity-25',
                isSelected && 'z-20 ring-2 ring-foreground ring-offset-2',
                isBlocked && 'ring-2 ring-amber-500',
                move && 'z-30 shadow-md'
              )}
              style={{
                right: `${startPct}%`,
                width: `${widthPct}%`,
                top: msToPx(
                  Math.max(shown.arrivalMs, startMs),
                  startMs,
                  pxPerHour
                ),
                height: msToPx(
                  shown.departureMs,
                  Math.max(shown.arrivalMs, startMs),
                  pxPerHour
                ),
              }}>
              {isBlocked && (
                <div className='absolute -bottom-5 left-0 z-40 whitespace-nowrap rounded bg-amber-500 px-1 text-[10px] text-black'>
                  {t('berthPlan.cutoffBlocked')}
                </div>
              )}
              {shown.extendsBefore && (
                <ChevronsRight
                  className='absolute right-0 top-0 size-3'
                  aria-label={t('berthPlan.extendsPastQuay')}
                />
              )}
              {shown.extendsAfter && (
                <ChevronsLeft
                  className='absolute left-0 top-0 size-3'
                  aria-label={t('berthPlan.extendsPastQuay')}
                />
              )}
              <div className='flex items-start justify-between gap-1'>
                <span className='truncate text-xs font-medium'>
                  {visit.ship_id}
                </span>
                {hasConflict && (
                  <TriangleAlert
                    className='size-3 shrink-0 text-destructive'
                    aria-label={t('berthPlan.conflict')}
                  />
                )}
              </div>
              <div className='truncate'>
                {t('berthPlan.eta', {
                  time: formatBlockTime(visit.arrival_time, i18n.language),
                })}
              </div>
              <div className='truncate'>
                {t('berthPlan.etd', {
                  time: formatBlockTime(visit.departure_time, i18n.language),
                })}
              </div>
              {moves && <div className='truncate'>{movesText}</div>}
            </div>
          </TooltipTrigger>
        </PopoverAnchor>
        <TooltipContent
          side='top'
          className='z-[110] flex flex-col gap-0.5 text-left'>
          <span className='font-semibold'>
            {t('berthPlan.tooltip.title', {
              shipId: visit.ship_id,
              service: visit.shipping_line_id,
              id: visit.id,
            })}
          </span>
          <span>
            {t('berthPlan.tooltip.times', {
              arrival: formatDateForDisplay(visit.arrival_time),
              departure: formatDateForDisplay(visit.departure_time),
            })}
          </span>
          <span>
            {moves
              ? t('berthPlan.tooltip.bollardsWithMoves', {
                  fore: visit.bollard_fore,
                  aft: visit.bollard_aft,
                  moves: movesText,
                })
              : t('berthPlan.tooltip.bollards', {
                  fore: visit.bollard_fore,
                  aft: visit.bollard_aft,
                })}
          </span>
          {block.isLocked && (
            <span className='opacity-70'>{t('berthPlan.tooltip.locked')}</span>
          )}
          {canMove && (
            <span className='opacity-70'>{t('berthPlan.tooltip.hint')}</span>
          )}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side='right'
        align='start'
        collisionPadding={16}
        className='z-[110] w-80'
        onOpenAutoFocus={(event) => {
          // Opened from find or the conflict stepper: keep focus there so
          // Enter / the arrows keep stepping instead of hitting Close.
          if (
            document.activeElement?.closest(`[${STEPS_SELECTION_ATTRIBUTE}]`)
          ) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          const { target } = event;
          if (
            target instanceof Element &&
            target.closest(`[${STEPS_SELECTION_ATTRIBUTE}]`)
          ) {
            event.preventDefault();
          }
        }}>
        {editor}
      </PopoverContent>
    </Popover>
  );
};

export default BerthPlanBlock;
